import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import {
  DateRangePickerComponent,
  DatePickerComponent,
} from "@syncfusion/ej2-react-calendars";
import {
  FormControl,
  Select,
  MenuItem,
  Switch,
  Button,
  Typography,
} from "@mui/material";
import { historyStartDate } from "../../utils/historyWindow";
import "./ChartControls.css"; // aynısını kullanıyoruz

/* =========================
   TIME DATA
========================= */

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

/* =========================
   HELPERS
========================= */

const toDate = (v) => (v ? dayjs(v, "YYYY-MM-DD").toDate() : null);
const toKey = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "");

function formatSeconds(sec = 0) {
  const m = String(Math.floor(sec / 60)).padStart(2, "0");
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s}`;
}

/* =========================
   TIME PICKER BOX
========================= */

function TimePickerBox({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popupRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const [h = "00", m = "00"] = (value || "00:00").split(":");

  useEffect(() => {
    function handleOutside(e) {
      const a = anchorRef.current;
      const p = popupRef.current;
      if ((a && a.contains(e.target)) || (p && p.contains(e.target))) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  function openPopup() {
    const r = anchorRef.current.getBoundingClientRect();
    setPos({
      top: r.bottom + 6 + window.scrollY,
      left: r.right - 140 + window.scrollX,
    });
    setOpen(true);
  }

  return (
    <div className="toolbar-group time-wrapper" ref={anchorRef}>
      <span className="toolbar-label">{label}</span>

      <div
        className={`time-box ${open ? "active" : ""}`}
        onClick={() => (open ? setOpen(false) : openPopup())}
      >
        <span className="time-icon">🕒</span>
        <span className="time-value">{h}:{m}</span>
      </div>

      {open &&
        createPortal(
          <div
            ref={popupRef}
            className="time-popup"
            style={{ position: "absolute", top: pos.top, left: pos.left, zIndex: 10000 }}
          >
            <div className="time-column">
              {HOURS.map((hh) => (
                <div
                  key={hh}
                  className={`time-item ${hh === h ? "selected" : ""}`}
                  onClick={() => onChange?.(`${hh}:${m}`)}
                >
                  {hh}
                </div>
              ))}
            </div>

            <div className="time-column">
              {MINUTES.map((mm) => (
                <div
                  key={mm}
                  className={`time-item ${mm === m ? "selected" : ""}`}
                  onClick={() => {
                    onChange?.(`${h}:${mm}`);
                    setOpen(false);
                  }}
                >
                  {mm}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* =========================
   MAIN TOOLBAR
========================= */

export default function SegmentToolbar({
  channelId,
  interval,
  onIntervalChange,

  mode,

  startDateInput,
  endDateInput,
  onStartDateChange,
  onEndDateChange,

  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,

  refreshEnabled,
  onRefreshToggle,
  refreshIntervalMin,
  onRefreshIntervalChange,
  countdown,
  onManualRefresh,
}) {
  const today = new Date();
  const earliest = historyStartDate(channelId);
  const singleDayOnly = mode === "minute";

  return (
    <div className="chart-controls">
      <div className="chart-toolbar">

        {/* LEFT */}
        <div className="toolbar-left">

          {/* Interval */}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={interval} onChange={(e) => onIntervalChange(e.target.value)}>
              <MenuItem value="Weekly">Weekly</MenuItem>
              <MenuItem value="Daily">Daily</MenuItem>
              <MenuItem value="Custom">Custom</MenuItem>
            </Select>
          </FormControl>

          {/* Date */}
          <div className="toolbar-group">
            <span className="toolbar-label">Date</span>

            {singleDayOnly ? (
              <DatePickerComponent
                value={toDate(startDateInput)}
                min={earliest}
                max={today}
                showClearButton={false}
                cssClass="dashboard-range-picker"
                format="dd/MM/yyyy"
                change={(e) => e?.value && onStartDateChange?.(toKey(e.value))}
              />
            ) : (
              <DateRangePickerComponent
                startDate={toDate(startDateInput)}
                endDate={toDate(endDateInput)}
                min={earliest}
                max={today}
                showClearButton={false}
                cssClass="dashboard-range-picker"
                format="dd/MM/yyyy"
                change={(e) => {
                  if (e?.startDate) onStartDateChange?.(toKey(e.startDate));
                  if (e?.endDate) onEndDateChange?.(toKey(e.endDate));
                }}
              />
            )}
          </div>

          {/* Time */}
          <TimePickerBox label="From" value={startTime} onChange={onStartTimeChange} />
          <TimePickerBox label="To" value={endTime} onChange={onEndTimeChange} />
        </div>

        {/* RIGHT */}
        <div className="toolbar-right">
          <Typography fontWeight={700}>Refresh Timer</Typography>

          <Switch checked={refreshEnabled} onChange={(e) => onRefreshToggle(e.target.checked)} />

          {refreshEnabled && (
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select
                value={refreshIntervalMin}
                onChange={(e) => onRefreshIntervalChange(Number(e.target.value) || 1)}
              >
                <MenuItem value={1}>1 min</MenuItem>
                <MenuItem value={5}>5 min</MenuItem>
                <MenuItem value={10}>10 min</MenuItem>
                <MenuItem value={20}>20 min</MenuItem>
              </Select>
            </FormControl>
          )}

          <Button size="small" variant="contained" onClick={onManualRefresh}>
            {refreshEnabled ? formatSeconds(countdown) : "Manual Refresh"}
          </Button>
        </div>
      </div>
    </div>
  );
}
